import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchLessonsByDateAsync,
  createLessonAsync,
  updateLessonAsync,
  addVocabularyAsync,
  deleteVocabularyAsync,
  fetchGroupStudentsAsync,
  clearError,
  clearStudents
} from '../store/slices/lessonsSlice';
import type { Student } from '../store/slices/lessonsSlice';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { formatDateEuropean } from '../utils/dateUtils';
import { getApiErrorMessage } from '../utils/apiError';
import './Students.css';
import './GroupForm.css';

// Add interface for validation errors
interface ValidationErrors {
  group?: string;
  vocabulary?: string;
  general?: string;
}

const LessonEditor: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const { lessons, loading, error, students, loadingStudents } = useAppSelector((state) => state.lessons);
  const { groups } = useAppSelector((state) => state.groups);

  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const groupParam = searchParams.get('group');
  
  const [selectedGroupId, setSelectedGroupId] = useState<string>(groupParam || '');
  const [topic, setTopic] = useState<string>('');
  const [link, setLink] = useState<string>('');
  const [newWords, setNewWords] = useState<string>('');
  const [absentStudentIds, setAbsentStudentIds] = useState<number[]>([]);
  const [canceled, setCanceled] = useState<boolean>(false);
  // Add validation errors state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  // Local state for date picker (doesn't trigger reload until button click)
  const [selectedDate, setSelectedDate] = useState<string>(date);
  // Cancel requires explicit confirmation (it shifts subscription end dates)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState<boolean>(false);

  // Load groups and lessons for the date
  useEffect(() => {
    dispatch(fetchGroupsAsync({ page: 1, limit: 100 }));
    
    // Pass group parameter if available
    const fetchParams: { date: string; groupId?: number } = { date };
    if (groupParam) {
      fetchParams.groupId = parseInt(groupParam);
    }
    
    dispatch(fetchLessonsByDateAsync(fetchParams));
  }, [dispatch, date, groupParam]);

  // Find existing lesson for selected group and date
  const existingLesson = selectedGroupId ?
    lessons.find(lesson => lesson.groupId === parseInt(selectedGroupId) && lesson.date === date) :
    null;

  // isEditing / currentLessonId are purely derived from the located lesson.
  const isEditing = !!existingLesson;
  const currentLessonId = existingLesson?.id ?? null;

  // Reset editable form state (topic/link/absences) whenever the located lesson
  // changes. Done during render via the "adjust state when a value changes"
  // pattern instead of an effect, so it stays behavior-identical without a
  // synchronous setState inside useEffect.
  const [syncedLesson, setSyncedLesson] = useState(existingLesson);
  if (syncedLesson !== existingLesson) {
    setSyncedLesson(existingLesson);
    if (existingLesson) {
      setTopic(existingLesson.topic || '');
      setLink(existingLesson.link || '');
      setCanceled(!!existingLesson.canceled);
      // Set absent students from existing lesson
      const currentAbsentIds = existingLesson.absences?.map(absence => absence.studentId) || [];
      setAbsentStudentIds(currentAbsentIds);
    } else {
      setTopic('');
      setLink('');
      setCanceled(false);
      setAbsentStudentIds([]);
    }
  }

  // Fetch students when group is selected
  useEffect(() => {
    if (selectedGroupId) {
      dispatch(fetchGroupStudentsAsync(parseInt(selectedGroupId)));
    } else {
      dispatch(clearStudents());
      // Absences are reset by the lesson-sync logic above (existingLesson is
      // null when no group is selected).
    }
  }, [dispatch, selectedGroupId]);

  // Helper: check if a student is on vacation for the given date
  const isStudentOnVacation = useCallback((student: Student, lessonDate: string): boolean => {
    if (!student.vacations || student.vacations.length === 0) return false;
    return student.vacations.some((v) => lessonDate >= v.dateFrom && lessonDate <= v.dateTo);
  }, []);

  // Helper: student is in a paid freeze ("vacation" subscription) covering the lesson date.
  // Frozen students are grayed out and excluded from attendance entirely — never marked absent,
  // so freeze time is not counted anywhere.
  const isStudentFrozen = useCallback((student: Student, lessonDate: string): boolean => {
    if (!student.vacations || student.vacations.length === 0) return false;
    return student.vacations.some(
      (v) => v.vacationType?.isFreeze && lessonDate >= v.dateFrom && lessonDate <= v.dateTo
    );
  }, []);

  // Helper: student has quit on or before the lesson date (i.e. no longer a member of the group)
  const hasQuitByDate = useCallback((student: Student, lessonDate: string): boolean => {
    if (!student.quitDate) return false;
    const quitDateStr = typeof student.quitDate === 'string'
      ? student.quitDate.slice(0, 10)
      : new Date(student.quitDate).toISOString().slice(0, 10);
    return lessonDate >= quitDateStr;
  }, []);

  // Auto-mark students on vacation as absent when creating a new lesson.
  // Skip students who have already quit — the backend rejects them, and they
  // aren't members of the group on that date anyway.
  //
  // Runs during render via the "adjust state when inputs change" pattern,
  // keyed on the inputs the original effect depended on. This merges the
  // vacation absences once per fetch/date change without clobbering subsequent
  // user toggles, matching the previous effect behavior.
  const vacationSyncKey = `${students.length > 0 ? students.map(s => s.id).join(',') : ''}|${date}|${isEditing}`;
  const [syncedVacationKey, setSyncedVacationKey] = useState<string | null>(null);
  if (syncedVacationKey !== vacationSyncKey) {
    setSyncedVacationKey(vacationSyncKey);
    if (!isEditing && students.length > 0 && date) {
      const vacationAbsentIds = students
        .filter(s => isStudentOnVacation(s, date) && !isStudentFrozen(s, date) && !hasQuitByDate(s, date))
        .map(s => s.id);
      if (vacationAbsentIds.length > 0) {
        setAbsentStudentIds(prev => {
          const merged = new Set([...prev, ...vacationAbsentIds]);
          return Array.from(merged);
        });
      }
    }
  }

  const handleGroupChange = useCallback((newGroupId: string) => {
    setSelectedGroupId(newGroupId);
    // Clear group validation error when user selects a group
    if (newGroupId && validationErrors.group) {
      setValidationErrors(prev => ({ ...prev, group: undefined }));
    }
    // Update URL to reflect group selection
    const newParams = new URLSearchParams(searchParams);
    if (newGroupId) {
      newParams.set('group', newGroupId);
    } else {
      newParams.delete('group');
    }
    navigate(`/lesson?${newParams.toString()}`, { replace: true });
  }, [navigate, searchParams, validationErrors.group]);

  const handleDateChange = useCallback(async () => {
    // Only act if the date actually changed
    if (!selectedDate || selectedDate === date) return;

    if (isEditing && currentLessonId) {
      // Update the lesson's date in the DB without reloading
      try {
        await dispatch(updateLessonAsync({
          id: currentLessonId,
          data: { date: selectedDate }
        })).unwrap();
        // Update URL to reflect new date
        const newParams = new URLSearchParams(searchParams);
        newParams.set('date', selectedDate);
        navigate(`/lesson?${newParams.toString()}`, { replace: true });
      } catch (error) {
        setValidationErrors({ general: getApiErrorMessage(error, 'Failed to update lesson date') });
      }
    } else {
      // Not editing — navigate to load lessons for the new date
      const newParams = new URLSearchParams(searchParams);
      newParams.set('date', selectedDate);
      navigate(`/lesson?${newParams.toString()}`);
    }
  }, [searchParams, date, selectedDate, isEditing, currentLessonId, dispatch, navigate]);

  const handleSaveLesson = useCallback(async () => {
    // Clear previous errors
    setValidationErrors({});

    if (!selectedGroupId) {
      setValidationErrors({ group: 'Please select a group' });
      return;
    }

    const lessonData = {
      groupId: parseInt(selectedGroupId),
      date,
      topic: topic.trim() || undefined,
      link: link.trim() || undefined,
      canceled,
      // Nobody is absent from a canceled lesson
      absentStudentIds: canceled ? [] : absentStudentIds
    };

    try {
      if (isEditing && currentLessonId) {
        // Update existing lesson
        await dispatch(updateLessonAsync({
          id: currentLessonId,
          data: {
            topic: lessonData.topic,
            link: lessonData.link,
            canceled: lessonData.canceled,
            absentStudentIds: lessonData.absentStudentIds
          }
        })).unwrap();
      } else {
        // Create new lesson
        await dispatch(createLessonAsync(lessonData)).unwrap();
      }
      
      // Refresh lessons list
      const fetchParams: { date: string; groupId?: number } = { date };
      if (selectedGroupId) {
        fetchParams.groupId = parseInt(selectedGroupId);
      }
      dispatch(fetchLessonsByDateAsync(fetchParams));
      
      // Clear any existing errors on success
      setValidationErrors({});
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to save lesson. Please try again.');
      setValidationErrors({ general: errorMessage });
    }
  }, [dispatch, selectedGroupId, date, topic, link, canceled, isEditing, currentLessonId, absentStudentIds]);

  // Cancel / reactivate is a one-click action, not a form toggle: it persists
  // immediately. A lesson that doesn't exist yet is created as canceled straight
  // away (the slot row is the cancelation marker).
  const handleToggleCanceled = useCallback(async () => {
    setValidationErrors({});

    if (!selectedGroupId) {
      setValidationErrors({ group: 'Please select a group' });
      return;
    }

    const nextCanceled = !canceled;
    // Reflect the new status optimistically; nobody is absent from a canceled lesson
    setCanceled(nextCanceled);
    if (nextCanceled) {
      setAbsentStudentIds([]);
    }

    try {
      if (isEditing && currentLessonId) {
        await dispatch(updateLessonAsync({
          id: currentLessonId,
          data: {
            canceled: nextCanceled,
            absentStudentIds: nextCanceled ? [] : absentStudentIds
          }
        })).unwrap();
      } else {
        // No lesson yet — create it already canceled
        await dispatch(createLessonAsync({
          groupId: parseInt(selectedGroupId),
          date,
          topic: topic.trim() || undefined,
          link: link.trim() || undefined,
          canceled: true,
          absentStudentIds: []
        })).unwrap();
      }

      dispatch(fetchLessonsByDateAsync({ date, groupId: parseInt(selectedGroupId) }));
    } catch (error) {
      setCanceled(canceled); // revert the optimistic change
      setValidationErrors({ general: getApiErrorMessage(error, 'Failed to update lesson status') });
    }
  }, [dispatch, selectedGroupId, date, topic, link, absentStudentIds, canceled, isEditing, currentLessonId]);

  const toggleStudentAbsence = useCallback((studentId: number) => {
    setAbsentStudentIds(prev => {
      if (prev.includes(studentId)) {
        // Remove from absent list
        return prev.filter(id => id !== studentId);
      } else {
        // Add to absent list
        return [...prev, studentId];
      }
    });
  }, []);

  const handleAddVocabulary = useCallback(async () => {
    // Clear previous vocabulary errors
    setValidationErrors(prev => ({ ...prev, vocabulary: undefined }));

    if (!currentLessonId || !newWords.trim()) {
      setValidationErrors(prev => ({ 
        ...prev, 
        vocabulary: 'Please save the lesson first and add some words' 
      }));
      return;
    }

    const words = newWords
      .split(',')
      .map(word => word.trim().replace(/,$/, '')) // Trim spaces and remove trailing commas
      .filter(word => word.length > 0);

    if (words.length === 0) {
      setValidationErrors(prev => ({ 
        ...prev, 
        vocabulary: 'Please add some words' 
      }));
      return;
    }

    try {
      await dispatch(addVocabularyAsync({ 
        lessonId: currentLessonId, 
        words 
      })).unwrap();
      
      setNewWords('');
      // Refresh lessons to get updated vocabulary
      const fetchParams: { date: string; groupId?: number } = { date };
      if (selectedGroupId) {
        fetchParams.groupId = parseInt(selectedGroupId);
      }
      dispatch(fetchLessonsByDateAsync(fetchParams));
      
      // Clear vocabulary errors on success
      setValidationErrors(prev => ({ ...prev, vocabulary: undefined }));
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to add vocabulary. Please try again.');
      setValidationErrors(prev => ({ ...prev, vocabulary: errorMessage }));
    }
  }, [dispatch, currentLessonId, newWords, date, selectedGroupId]);

  const handleDeleteVocabulary = useCallback(async (vocabularyId: number) => {
    if (!confirm('Are you sure you want to delete this word?')) {
      return;
    }

    try {
      await dispatch(deleteVocabularyAsync(vocabularyId)).unwrap();
      // Refresh lessons to get updated vocabulary
      const fetchParams: { date: string; groupId?: number } = { date };
      if (selectedGroupId) {
        fetchParams.groupId = parseInt(selectedGroupId);
      }
      dispatch(fetchLessonsByDateAsync(fetchParams));
      // Clear any vocabulary errors on successful deletion
      setValidationErrors(prev => ({ ...prev, vocabulary: undefined }));
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, 'Failed to delete vocabulary word. Please try again.');
      setValidationErrors(prev => ({ ...prev, vocabulary: errorMessage }));
    }
  }, [dispatch, date, selectedGroupId]);

  const formatDate = useCallback((dateString: string) => {
    return formatDateEuropean(dateString);
  }, []);

  const selectedGroup = selectedGroupId ? groups.find(g => g.id === parseInt(selectedGroupId)) : null;

  const combinedStudents = useMemo(() => {
    const map = new Map<number, Student>();
    students.forEach(s => map.set(s.id, s));
    const existingAbsentIds = new Set<number>(
      existingLesson?.absences?.map(a => a.studentId) ?? []
    );
    if (existingLesson?.absences) {
      existingLesson.absences.forEach(a => {
        const s = a.student;
        if (s && !map.has(s.id)) {
          map.set(s.id, {
            id: s.id,
            firstName: s.firstName,
            lastName: s.lastName,
            surname: s.surname,
            startDate: null,
            quitDate: s.quitDate ?? null
          });
        }
      });
    }
    return Array.from(map.values()).filter(student => {
      // Hide students whose start date is after the lesson date
      if (student.startDate) {
        const startDateStr = typeof student.startDate === 'string'
          ? student.startDate.slice(0, 10)
          : '';
        if (startDateStr > date) return false;
      }
      // Hide students who joined THIS group after the lesson date — they weren't in
      // this group yet. (Historical absences keep them visible regardless.)
      if (student.attributedOn && !existingAbsentIds.has(student.id)) {
        const attributedStr = String(student.attributedOn).slice(0, 10);
        if (attributedStr > date) return false;
      }
      // Hide students who quit on/before the lesson date, unless they were
      // already absent in this lesson (historical record — keep them visible).
      if (hasQuitByDate(student, date) && !existingAbsentIds.has(student.id)) {
        return false;
      }
      return true;
    });
  }, [students, existingLesson, date, hasQuitByDate]);

  if (loading && groups.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>
            {isEditing ? 'Edit Lesson' : 'Create Lesson'} - {formatDate(date)}
          </h1>
          <p>
            {selectedGroup 
              ? `${selectedGroup.name || `${selectedGroup.level.toUpperCase()} Group`}`
              : 'Select a group to create or edit lesson'
            }
          </p>
        </div>
        <button 
          onClick={() => navigate('/lessons')}
          className="btn-secondary"
        >
          Back to Lessons
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <div className="form-container">
        {/* Date Selection */}
        <div className="form-group">
          <label htmlFor="lesson-date" className="form-label">
            Lesson Date <span className="required">*</span>
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <input
              id="lesson-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="form-input"
              style={{ flex: 1 }}
            />
            <button
              onClick={handleDateChange}
              disabled={selectedDate === date}
              className="btn-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Apply
            </button>
          </div>
          {selectedDate !== date && (
            <small className="form-help" style={{ color: '#f59e0b' }}>
              {isEditing
                ? `Click "Apply" to move this lesson to ${formatDateEuropean(selectedDate)}`
                : `Click "Apply" to load lessons for ${formatDateEuropean(selectedDate)}`}
            </small>
          )}
        </div>

        {/* Group Selection */}
        <div className="form-group">
          <label htmlFor="group-select" className="form-label">
            {isEditing ? 'Group' : 'Select Group'} <span className="required">*</span>
          </label>
          {isEditing ? (
            // Show group as read-only when editing
            <div className="form-input-readonly">
              {selectedGroup 
                ? `${selectedGroup.name || `${selectedGroup.level.toUpperCase()} Group`} (${selectedGroup.level.toUpperCase()})`
                : 'No group selected'
              }
            </div>
          ) : (
            // Show dropdown only for creation, and disable once selected
            <select
              id="group-select"
              value={selectedGroupId}
              onChange={(e) => handleGroupChange(e.target.value)}
              disabled={!!selectedGroupId} // Disable once a group is selected
              className={`form-select ${validationErrors.group ? 'error' : ''} ${selectedGroupId ? 'disabled' : ''}`}
            >
              <option value="">Choose a group...</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name || `${group.level.toUpperCase()} Group`} ({group.level.toUpperCase()})
                </option>
              ))}
            </select>
          )}
          {validationErrors.group && (
            <span className="error-text">{validationErrors.group}</span>
          )}
          {!isEditing && selectedGroupId && (
            <small className="form-help">
              Group locked. <button 
                type="button" 
                onClick={() => handleGroupChange('')}
                className="btn-link-small"
              >
                Reset
              </button>
            </small>
          )}
        </div>

        {selectedGroupId && (
          <>
            {canceled && (
              <div className="lesson-canceled-banner">
                This lesson is canceled — it does not consume subscription lessons, and absences are cleared.
              </div>
            )}

            {/* Lesson Details */}
            <div className="form-group">
              <label htmlFor="topic" className="form-label">
                Lesson Topic (Optional)
              </label>
              <input
                id="topic"
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Enter lesson topic..."
                className="form-input"
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label htmlFor="link" className="form-label">
                Lesson Link (Optional)
              </label>
              <input
                id="link"
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://example.com/lesson"
                className="form-input"
                maxLength={500}
              />
            </div>

            {/* Absences Section */}
            <div className="form-group">
              <h3>Absences</h3>
              {loadingStudents ? (
                <div className="loading-text">Loading students...</div>
              ) : combinedStudents.length > 0 ? (
                <div className={`students-list ${canceled ? 'students-list--disabled' : ''}`}>
                  <p className="section-description">
                    {canceled
                      ? 'Lesson is canceled — nobody is absent from a canceled lesson.'
                      : 'Click on students to mark them as absent. Absent students will be highlighted.'}
                  </p>
                  <div className="students-grid">
                    {combinedStudents.map((student) => {
                      const isAbsent = absentStudentIds.includes(student.id);
                      const quitDateStr = student.quitDate ? (typeof student.quitDate === 'string' ? student.quitDate.slice(0, 10) : '') : null;
                      const frozen = isStudentFrozen(student, date);
                      // Frozen students are excluded from attendance: not clickable, never absent.
                      const isDisabled = frozen || (quitDateStr ? (date >= quitDateStr) : false);
                      const onVacation = !frozen && isStudentOnVacation(student, date);
                      return (
                        <div
                          key={student.id}
                          onClick={() => { if (!isDisabled && !canceled) { toggleStudentAbsence(student.id); } }}
                          className={`student-item ${isDisabled ? 'disabled' : (isAbsent ? 'absent' : 'present')}`}
                          title={frozen
                            ? 'Subscription frozen — excluded from attendance'
                            : isDisabled
                              ? `Quitted${quitDateStr ? ` on ${formatDate(quitDateStr)}` : ''}`
                              : onVacation
                                ? `On vacation — click to mark as ${isAbsent ? 'present' : 'absent'}`
                                : `Click to mark as ${isAbsent ? 'present' : 'absent'}`}
                        >
                          <span className="student-name">
                            <span>{student.firstName} {student.lastName}</span>
                            {student.surname && <span className="student-surname">{student.surname}</span>}
                            {frozen && <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#60a5fa' }}>❄ Frozen</span>}
                            {onVacation && <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#f59e0b' }}>🏖 On vacation</span>}
                          </span>
                          <span className="student-status">
                            {frozen ? '❄' : (isDisabled ? '🚫 Quitted' : (isAbsent ? '❌' : '✅'))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {absentStudentIds.length > 0 && (
                    <div className="absence-summary">
                      <p>
                        <strong>Absent students ({absentStudentIds.length}):</strong>{' '}
                        {combinedStudents
                          .filter(s => absentStudentIds.includes(s.id))
                          .map(s => `${s.firstName} ${s.lastName}${s.surname ? ` ${s.surname}` : ''}`)
                          .join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              ) : selectedGroupId ? (
                <p className="no-students">No students found in this group.</p>
              ) : null}
            </div>

            {/* General Error Message */}
            {validationErrors.general && (
              <div className="error-message">
                {validationErrors.general}
              </div>
            )}

            <div className="form-group">
              <div className="lesson-actions">
                <button
                  onClick={handleSaveLesson}
                  disabled={loading}
                  className="btn-success"
                >
                  {loading ? 'Saving...' : (isEditing ? 'Update Lesson' : 'Create Lesson')}
                </button>
                <button
                  type="button"
                  onClick={() => { if (canceled) { handleToggleCanceled(); } else { setConfirmCancelOpen(true); } }}
                  disabled={loading}
                  className={`btn-small lesson-cancel-btn ${canceled ? 'lesson-cancel-btn--reactivate' : ''}`}
                  title={canceled
                    ? 'Reactivate this lesson so it consumes a subscription lesson again'
                    : 'Cancel this lesson so it no longer consumes a subscription lesson'}
                >
                  {canceled ? 'Reactivate lesson' : 'Cancel lesson'}
                </button>
              </div>
            </div>

            {/* Vocabulary Section */}
            {(isEditing && currentLessonId) && (
              <div className="vocabulary-section">
                <h3>Vocabulary</h3>
                
                {/* Existing Vocabulary */}
                {existingLesson?.vocabulary && existingLesson.vocabulary.length > 0 && (
                  <div className="existing-vocabulary">
                    <h4>Current Words ({existingLesson.vocabulary.length})</h4>
                    <div className="vocabulary-list">
                      {existingLesson.vocabulary.map((vocab) => (
                        <div key={vocab.id} className="vocabulary-item">
                          <span className="vocabulary-word">{vocab.word}</span>
                          <button
                            onClick={() => handleDeleteVocabulary(vocab.id)}
                            className="btn-small btn-danger"
                            title="Delete word"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Vocabulary */}
                <div className="add-vocabulary">
                  <h4>Add New Words</h4>
                  <div className="form-group">
                    <label htmlFor="new-words" className="form-label">
                      New Vocabulary (comma-separated)
                    </label>
                    <textarea
                      id="new-words"
                      value={newWords}
                      onChange={(e) => {
                        setNewWords(e.target.value);
                        // Clear vocabulary validation error when user starts typing
                        if (validationErrors.vocabulary) {
                          setValidationErrors(prev => ({ ...prev, vocabulary: undefined }));
                        }
                      }}
                      placeholder="word1, word2, word3, phrase with spaces"
                      className={`form-textarea ${validationErrors.vocabulary ? 'error' : ''}`}
                      rows={6}
                    />
                    {validationErrors.vocabulary && (
                      <span className="error-text">{validationErrors.vocabulary}</span>
                    )}
                    {newWords.trim() && (
                      <div className="word-preview">
                        <small className="preview-label">Words to be added:</small>
                        <div className="preview-words">
                          {newWords
                            .split(',')
                            .map(word => word.trim().replace(/,$/, ''))
                            .filter(word => word.length > 0)
                            .map((word, index) => (
                              <span key={index} className="preview-word">
                                {word}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={handleAddVocabulary}
                    disabled={loading || !newWords.trim()}
                    className="btn-success"
                  >
                    {loading ? 'Adding...' : 'Add Vocabulary'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmCancelOpen}
        title="Cancel lesson"
        message={`Cancel the lesson${selectedGroup ? ` for ${selectedGroup.name || `${selectedGroup.level.toUpperCase()} Group`}` : ''} on ${formatDate(date)}?\n\nIt will no longer consume a subscription lesson — affected subscriptions' estimated end dates shift out by one slot — and any marked absences are cleared.${isEditing ? '' : '\n\nThe lesson will be created in canceled state.'}`}
        confirmText="Yes, cancel lesson"
        cancelText="Keep lesson"
        dangerous
        onConfirm={() => { setConfirmCancelOpen(false); handleToggleCanceled(); }}
        onCancel={() => setConfirmCancelOpen(false)}
      />

      {loading && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default LessonEditor; 